import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { ArrowLeft, ChevronRight, LoaderCircle, Minus, Plus, ShoppingCart } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { OtherSellersSheet, ProductGrid } from "@/components";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { addToCart } from "@/store/slices/appSlice";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import parse from "html-react-parser";

import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { getProductInfo } from "@/graphql/queries";
import { Category, ProductVariant } from "@/types/AppTypes";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import capitalizeWords from "@/utils/helper/capitalizeWords";
import { useCartManagement } from "@/hooks/useCartManagement";

const ProductDetails = () => {
  const [ownFetchedVariants, setOwnFetchedVariants] = useState<ProductVariant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addToCart : addToCartBackend, isAuthenticated } = useCartManagement();
  const [directProduct, setDirectProduct] = useState<any>(null);
  const [hasFetchedOwn, setHasFetchedOwn] = useState(false);
  const [otherSellers, setOtherSellers] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { productsForDisplay, enrichedProductList, brands, categories, brandsMap } = useAppSelector(
    (state) => state.app
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(undefined);
  const dispatch = useAppDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const reduxProduct = useMemo(
    () => productsForDisplay.find((product) => product?.product_id === id),
    [id, productsForDisplay]
  );

  let product = useMemo(
    () => (hasFetchedOwn ? directProduct : reduxProduct || directProduct),
    [hasFetchedOwn, directProduct, reduxProduct]
  );


  useEffect(() => {
    if (!product && !hasFetchedOwn) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const fetchProduct = async () => {
        setIsLoading(true);
        try {
          const singleProductInfo = await executeGraphQLMutation({
            query: getProductInfo,
            variables: {
              id: id,
              filter: {
                is_active: { eq: true },
                is_deleted: { eq: false },
              },
            },

            signal: abortControllerRef?.current?.signal,
          });

          setDirectProduct(singleProductInfo || []);

          if (!abortControllerRef.current?.signal.aborted) {
            const singleProductVariants = (singleProductInfo?.variants || [])
              .map((v: ProductVariant) => {
                try {
                  return typeof v === "string" ? JSON.parse(v) : v;
                } catch (e) {
                  console.error("Failed to parse variant JSON:", v);
                  return null;
                }
              })
              .filter(Boolean);

            if (singleProductVariants.length > 0) {
              const updatedVariants = singleProductVariants.map((variant: ProductVariant) => ({
                ...variant,
                product_id: variant.product_id || id,
                seller_id: variant.seller_id || singleProductInfo?.seller_id || "",
                brand_id: singleProductInfo?.brand_id || "",
                category_id: singleProductInfo?.category_id || [],
                ...(variant?.name ? {} : { name: singleProductInfo?.name }),
              }));

              setOwnFetchedVariants(updatedVariants);

              setHasFetchedOwn(true);
            }
          }
        } catch (error) {
          console.error("Error fetching product:", error);
        } finally {
          if (!abortControllerRef.current?.signal.aborted) {
            setIsLoading(false);
          }
        }
      };

      fetchProduct();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [id, hasFetchedOwn]);

  const isLoadingData = isLoading;

  const enrichedProduct = useMemo(() => {
    if (!product?.product_id) return null;
    return enrichedProductList.find((p) => p.id === product.product_id) || null;
  }, [product, enrichedProductList]);

  const variants = useMemo(() => {
    const enrichedProductVariants = enrichedProduct?.variants || [];
    if (enrichedProductVariants.length > 0) {
      if (typeof enrichedProductVariants[0] !== "string") return enrichedProductVariants;
      return enrichedProductVariants
        ?.map((v: any) => JSON.parse(v))
        ?.map((variant: any) => ({
          ...variant,
          product_id: variant.product_id || id,
          seller_id: variant.seller_id || product?.seller_id || "",
          brand_id: product?.brand_id || "",
          category_id: product?.category_id || [],
          ...(variant?.name ? {} : { name: product?.name }),
        }))
        ?.filter((v) => v && v.is_active !== false);
    } else if (hasFetchedOwn && ownFetchedVariants.length > 0) {
      if (typeof ownFetchedVariants[0] !== "string") return ownFetchedVariants;
      return ownFetchedVariants
        ?.map((v: any) => JSON.parse(v))
        ?.filter((v) => v && v.is_active !== false);
    }

    return [];
  }, [enrichedProduct, ownFetchedVariants]);

  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});

  const attributeOptions = useMemo(() => {
    const attributeTypes: Record<string, Set<string>> = {};

    variants.forEach((variant) => {
      if (variant?.attributes) {
        Object.entries(variant.attributes).forEach(([attrType, attrValue]) => {
          if (!attributeTypes[attrType]) {
            attributeTypes[attrType] = new Set();
          }
          attributeTypes[attrType].add(attrValue as string);
        });
      }
    });

    return Object.fromEntries(
      Object.entries(attributeTypes).map(([type, values]) => [type, Array.from(values)])
    );
  }, [variants]);

  const currentVariant = useMemo(() => {
    if (!variants.length) return null;
    if (selectedVariantId) {
      const selected = variants.find((v) => v.id === selectedVariantId);
      if (selected) return selected;
    }
    const defaultVariant = variants.find((v) => v.show_by_default) || variants[0];
    return defaultVariant;
  }, [variants, selectedVariantId]);
  const handleAttributeSelect = (attrType: string, attrValue: string) => {
    const newAttributes = {
      ...selectedAttributes,
      [attrType]: attrValue,
    };

    let matchingVariant = variants.find((variant) => {
      if (!variant?.attributes) return false;

      return Object.entries(newAttributes).every(
        ([type, value]) => variant?.attributes[type] === value
      );
    });

    if (matchingVariant) {
      setSelectedVariantId(matchingVariant?.id);
      setSelectedAttributes(newAttributes);
      return;
    }

    matchingVariant = variants.find((variant) => {
      return variant.attributes && variant.attributes[attrType] === attrValue;
    });

    if (matchingVariant) {
      setSelectedVariantId(matchingVariant.id);

      setSelectedAttributes({
        ...matchingVariant.attributes,
      });
    } else {
      setSelectedAttributes(newAttributes);
    }
  };

  useEffect(() => {
    if (currentVariant?.attributes && Object.keys(selectedAttributes).length === 0) {
      setSelectedAttributes(currentVariant.attributes);
    }
  }, [currentVariant, selectedAttributes]);

  const relatedProducts = useMemo(
    () =>
      productsForDisplay.filter(
        (prdt) => prdt?.category?.id === product?.category?.id && prdt?.product_id !== id
      ),
    [product, id, productsForDisplay]
  );

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decreaseQuantity = () => {
    setQuantity((prev) => Math.max(prev - 1, 1));
  };

  const handleAddToCart = () => {
    if (!currentVariant) return;
    const productToAdd = { ...currentVariant, quantity  };
    if(isAuthenticated){
      addToCartBackend(productToAdd, quantity);
    }else{
      dispatch(addToCart(productToAdd));
    }
  };
  useEffect(() => {
    if (directProduct && (brands?.length > 0 || categories?.length > 0)) {
      let enrichedProduct = { ...directProduct };
      let wasEnriched = false;

      if (!enrichedProduct.brand && enrichedProduct.brand_id) {
        const brand = brands?.find((b) => b.id === enrichedProduct.brand_id);
        if (brand) {
          enrichedProduct.brand = brand;
          wasEnriched = true;
        }
      }

      if (!enrichedProduct.category && enrichedProduct.category_id) {
        const categoryObjects = enrichedProduct.category_id
          ?.map((catId: string) => categories?.find((c) => c.id === catId))
          .filter(Boolean);

        if (categoryObjects && categoryObjects.length > 0) {
          enrichedProduct.category = categoryObjects;
          wasEnriched = true;
        }
      }

      if (wasEnriched) {
        setDirectProduct(enrichedProduct);
      }
    }
  }, [brands, categories, directProduct]);

  if (!product && isLoadingData) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoaderCircle className="w-6 h-6 animate-spin mr-4 "></LoaderCircle>
        Fetching product details. This won’t take long!
      </div>
    );
  }

  if (!product && !isLoadingData) {
    return (
      <section className="flex h-screen justify-center items-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Product Not Found</h2>
          <p className="mb-4">The product you're looking for doesn't exist or has been removed.</p>
          <Link to="/products">
            <Button>Browse Products</Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div className="container mx-auto py-8 px-6 max-w-6xl">
      <div className="group h-10 md:px-4 py-2 mb-6  ">
        <Link to="/products" className=" text-sm font-medium ">
          <ArrowLeft className="group-hover:translate-x-[-5px] transition-transform duration-300 h-4 w-4 inline-block mr-2" />
          Back to Products
        </Link>
      </div>

      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
        <Carousel className="w-full sm:max-w-[30rem] md:max-w-[40rem] lg:max-w-[30rem] xs:max-w-[24rem] xxs:max-w-[18rem] max-w-[15rem] self-start sticky  mx-auto">
          <CarouselContent>
            {currentVariant?.images?.map((img: string, index: number) => (
              <CarouselItem key={index}>
                <div className="rounded-lg overflow-hidden border bg-white p-0 m-0">
                  <div className="border-b border-gray-200 h-full p-0 m-0">
                    <img
                      src={img}
                      alt={"Product"}
                      className="w-full h-full object-cover aspect-square"
                    />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hover:bg-black hover:text-white  " />
          <CarouselNext className="hover:bg-black hover:text-white  " />
        </Carousel>

        {/* product details */}
        <div className="md:ml-8 flex gap-4">
          <div className="flex-1">
            <div className="mb-2">
              <Breadcrumb>
                <BreadcrumbList className="sm:gap-1">
                  {/* Brand */}
                  {product?.brand_id &&  brandsMap && (
                    <>
                      <BreadcrumbItem>
                        <BreadcrumbLink
                          className="text-base font-normal hover:underline text-black"
                          asChild
                        >
                          <Link to={`/brand/${brandsMap[product.brand_id]?.id}`}>
                            {capitalizeWords(brandsMap[product.brand_id]?.name || "")}
                          </Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="">
                        <span className="text-gray-500 text-base">/</span>
                      </BreadcrumbSeparator>
                    </>
                  )}

                  {/* Categories */}
                  {Array.isArray(product?.category) && product.category.length > 0 ? (
                    product.category.map((category: Category, index: number) => (
                      <React.Fragment key={category?.id || index}>
                        {/* Add separator between categories */}
                        {index > 0 && (
                          <BreadcrumbSeparator className="">
                            <span className="text-gray-500 text-base">,</span>
                          </BreadcrumbSeparator>
                        )}
                        <BreadcrumbItem>
                          <BreadcrumbLink
                            className="text-base font-normal hover:underline text-black"
                            asChild
                          >
                            <Link to={`/category/${category?.id}`}>
                              {capitalizeWords(category?.name || "")}
                            </Link>
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                      </React.Fragment>
                    ))
                  ) : (
                    <BreadcrumbItem>
                      <BreadcrumbLink className="text-base font-normal"></BreadcrumbLink>
                    </BreadcrumbItem>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <h1 className="text-3xl font-bold mb-2">{enrichedProduct?.name}</h1>
            {/* <div className="text-2xl font-bold mb-6">{`$${currentVariant?.price}`}</div> */}

            {Object.entries(attributeOptions).map(([attrType, attrValues]) => (
              <div className="mb-6" key={attrType}>
                <h3 className="text-sm font-medium mb-2">{attrType}</h3>
                <div className="grid grid-cols-3 gap-2">
                  {attrValues.map((attrValue) => (
                    <Button
                      key={attrValue}
                      onClick={() => handleAttributeSelect(attrType, attrValue)}
                      className={`px-3 py-1 rounded-md border ${
                        selectedAttributes[attrType] === attrValue
                          ? "bg-black text-white"
                          : "bg-white text-black hover:bg-gray-100"
                      }`}
                    >
                      {attrValue}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
            <div className="mb-6">
              <div className="flex items-center">
                <Button
                  disabled={quantity === 1}
                  onClick={decreaseQuantity}
                  className="shadow-xs border hover:bg-icon-hover  hover:text-white bg-transparent text-black"
                >
                  <Minus className=" h-4 w-4" />
                </Button>
                <div className="w-16 text-center mx-2">{quantity}</div>
                <Button
                  onClick={increaseQuantity}
                  className="shadow-xs border hover:bg-icon-hover  hover:text-white  bg-transparent text-black"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button
              className="w-full bg-black hover:bg-gray-800 text-white shadow-xs mb-4"
              onClick={handleAddToCart}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Add to Cart
            </Button>
          </div>
        </div>
        {currentVariant && (
          <div className="hidden lg:block absolute -right-80 top-0 h-full w-72 ">
            <div className="rounded-lg border bg-white shadow-sm w-full p-4 flex flex-col">
              <div className="font-semibold text-md mb-2">Other sellers on Palette</div>
              <div className="border-t mb-2"></div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700"> ({otherSellers?.length})</span>
                  <span className="block text-xs text-gray-400 mr-2">from</span>
                  <span className="text-xl font-bold text-black">${currentVariant?.price}</span>
                </div>

                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      className="p-2 rounded-full hover:bg-gray-100"
                      aria-label="See other sellers"
                    >
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    </Button>
                  </SheetTrigger>
                  <OtherSellersSheet
                    mainProductDetails={product ?? {}}
                    currentVariant={currentVariant ?? {}}
                    setOtherSellers={setOtherSellers}
                    setSheetOpen={setSheetOpen}
                    isMainProductloading={isLoading}
                  />
                </Sheet>
              </div>
            </div>
          </div>
        )}
      </div>

      <section className="md:ml-8 md:py-20 py-4">
        {currentVariant?.description && (
          <>
            <h2 className="text-xl font-semibold mb-4 ">Product Information</h2>

            <div className=" text-palette-text">{parse(currentVariant?.description || "")}</div>
          </>
        )}
      </section>

      {/* Related Products */}

      <section className="mt-16 md:ml-8">
        <h2 className="text-2xl font-semibold mb-6">Related Products</h2>
        <div className="my-8 ">
          {relatedProducts?.length === 0 ? (
            <p className="text-gray-500">No related products found.</p>
          ) : (
            <ProductGrid
              gridItems={relatedProducts?.slice(0,9)}
              className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 "
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductDetails;
